/**
 * The "Ready to play" summary on `/play` (QA batch 3 #1): the selected deck (legend art, champion, card
 * counts, legality from `@play/catalog`'s `validateDeck`), the rules tier with what it means, the
 * automation from settings, the clock (owner decision Q26) and the opponent, then every problem inline
 * with its fix (`readiness.ts`). Presentational: `/play` passes everything in.
 */
import type { DeckIssue } from '@play/catalog';
import { CardImage, LOOK, StatusBadge, WELL } from '@play/ui';
import { Link } from '@tanstack/react-router';
import { AlertCircle, Bot, Check, Clock, ExternalLink, Info, Loader2, RefreshCw, Settings2, Users } from 'lucide-react';
import type { ReactNode } from 'react';
import type { CardFace } from '../../lib/cards';
import { faceSources } from '../../lib/cards';
import type { EnabledTier } from '../../lib/env';
import type { OpponentKind, ReadinessFlag } from './readiness';
import { TIER_INFO } from './TierPicker';

/** What the card shows (all derived by `/play`). */
export interface ReadinessCardProps {
  deckName: string | null;
  legend: CardFace | null;
  legendName: string | null;
  championName: string | null;
  counts: { main: number; runes: number; battlefields: number } | null;
  /** `null` while checking. */
  legal: boolean | null;
  issues: readonly DeckIssue[];
  tier: EnabledTier;
  tierLine: string;
  automation: string;
  clock: string;
  opponent: OpponentKind;
  opponentLine: string;
  flags: readonly ReadinessFlag[];
  /** Where the deck is edited (runebound.gg), and where new decks are built. */
  deckUrl: string | null;
  buildUrl: string;
  onRecheck: () => void;
  onPickDeck: () => void;
  onPickBotDeck: () => void;
}

function Row({ icon, label, children }: { icon: ReactNode; label: string; children: ReactNode }) {
  return (
    <div className="grid grid-cols-[18px_88px_minmax(0,1fr)] gap-x-2 items-start text-[12.5px] leading-[18px]">
      <span className="mt-[1px]" style={{ color: LOOK.muted }} aria-hidden>
        {icon}
      </span>
      <span style={{ color: LOOK.muted }}>{label}</span>
      <span style={{ color: LOOK.sub }}>{children}</span>
    </div>
  );
}

const fixLink = 'inline-flex items-center gap-1 font-semibold hover:underline';

/** The readiness card. */
export function ReadinessCard(p: ReadinessCardProps) {
  const fix = (f: ReadinessFlag): ReactNode => {
    switch (f.fix) {
      case 'build':
        return (
          <a href={p.buildUrl} target="_blank" rel="noreferrer" className={fixLink} style={{ color: '#b9adff' }}>
            Build a deck on runebound.gg <ExternalLink size={12} />
          </a>
        );
      case 'editDeck':
        return (
          <span className="inline-flex flex-wrap gap-x-3 gap-y-1">
            {p.deckUrl ? (
              <a href={p.deckUrl} target="_blank" rel="noreferrer" className={fixLink} style={{ color: '#b9adff' }}>
                Fix it on runebound.gg <ExternalLink size={12} />
              </a>
            ) : null}
            <button type="button" onClick={p.onRecheck} className={fixLink} style={{ color: '#b9adff' }}>
              <RefreshCw size={12} /> Check again
            </button>
            <button type="button" onClick={p.onPickDeck} className={fixLink} style={{ color: '#b9adff' }}>
              Pick another deck
            </button>
          </span>
        );
      case 'recheck':
        return (
          <button type="button" onClick={p.onRecheck} className={fixLink} style={{ color: '#b9adff' }}>
            <RefreshCw size={12} /> Check again
          </button>
        );
      case 'pickDeck':
        return (
          <button type="button" onClick={p.onPickDeck} className={fixLink} style={{ color: '#b9adff' }}>
            Pick a deck
          </button>
        );
      case 'pickBotDeck':
        return (
          <button type="button" onClick={p.onPickBotDeck} className={fixLink} style={{ color: '#b9adff' }}>
            Choose a bot deck
          </button>
        );
      case null:
        return null;
    }
  };
  return (
    <div className="flex flex-col gap-3.5" data-testid="readiness">
      <div className="flex gap-3">
        <span className="w-[64px] shrink-0">
          {p.legend ? (
            <CardImage sources={faceSources(p.legend, 240)} alt={p.legend.alt} rounded="rounded-[8px]" />
          ) : (
            <span className="block aspect-[744/1039] rounded-[8px]" style={{ background: WELL }} />
          )}
        </span>
        <span className="min-w-0 flex-1 flex flex-col gap-0.5">
          <span className="font-semibold text-[15px] leading-5 truncate" style={{ color: LOOK.text }}>
            {p.deckName ?? 'No deck selected'}
          </span>
          {p.legendName ? (
            <span className="text-[12.5px] truncate" style={{ color: LOOK.sub }}>
              {p.legendName}
              {p.championName ? ` · ${p.championName}` : ''}
            </span>
          ) : null}
          {p.counts ? (
            <span className="text-[11.5px] tabular-nums" style={{ color: LOOK.muted }}>
              {p.counts.main} main · {p.counts.runes} runes · {p.counts.battlefields} battlefields
            </span>
          ) : null}
          <span className="mt-1 flex">
            {p.deckName === null ? null : p.legal === null ? (
              <StatusBadge
                color={LOOK.muted}
                icon={<Loader2 size={11} className="animate-spin motion-reduce:animate-none" />}
              >
                Checking
              </StatusBadge>
            ) : p.legal ? (
              <StatusBadge color={LOOK.ok} icon={<Check size={11} />}>
                Legal in Standard
              </StatusBadge>
            ) : (
              <StatusBadge color={LOOK.bad} icon={<AlertCircle size={11} />}>
                Not legal
              </StatusBadge>
            )}
          </span>
        </span>
      </div>

      <div className="flex flex-col gap-2" data-testid="readiness-rows">
        <Row icon={<Info size={14} />} label="Rules">
          <span className="font-semibold" style={{ color: LOOK.text }}>
            Tier {p.tier} · {TIER_INFO[p.tier].name}
          </span>
          <span className="block">{p.tierLine}</span>
        </Row>
        <Row icon={<Settings2 size={14} />} label="Automation">
          {p.automation}{' '}
          {p.tier === 2 ? (
            <Link to="/settings" className="font-semibold hover:underline" style={{ color: '#b9adff' }}>
              Change
            </Link>
          ) : null}
        </Row>
        <Row icon={<Clock size={14} />} label="Clock">
          {p.clock}
        </Row>
        <Row icon={p.opponent === 'bot' ? <Bot size={14} /> : <Users size={14} />} label="Opponent">
          {p.opponentLine}
        </Row>
      </div>

      {p.flags.length > 0 ? (
        <ul className="flex flex-col gap-2" aria-label="Before you play" data-testid="readiness-flags">
          {p.flags.map((f) => (
            <li
              key={f.id}
              className="rounded-[10px] px-3 py-2 flex flex-col gap-1 text-[12.5px] leading-[18px]"
              style={{
                background: f.tone === 'bad' ? `${LOOK.bad}14` : 'rgba(124,92,255,0.08)',
                boxShadow: `inset 0 0 0 1px ${f.tone === 'bad' ? `${LOOK.bad}55` : 'rgba(124,92,255,0.3)'}`,
                color: LOOK.sub,
              }}
              role={f.tone === 'bad' ? 'alert' : undefined}
              data-testid={`readiness-flag-${f.id}`}
            >
              <span className="flex items-start gap-1.5" style={{ color: LOOK.text }}>
                {f.tone === 'bad' ? (
                  <AlertCircle size={14} className="mt-[2px] shrink-0" color={LOOK.bad} />
                ) : (
                  <Info size={14} className="mt-[2px] shrink-0" color="#b9adff" />
                )}
                {f.text}
              </span>
              {f.id === 'illegal' && p.issues.length > 0 ? (
                <ul className="flex flex-col gap-0.5 pl-5">
                  {p.issues.slice(0, 4).map((i, n) => (
                    <li key={n} className="text-[12px]">
                      {i.message}{' '}
                      <span className="mono" style={{ color: LOOK.faint }}>
                        {i.rule}
                      </span>
                    </li>
                  ))}
                  {p.issues.length > 4 ? <li className="text-[12px]">and {p.issues.length - 4} more</li> : null}
                </ul>
              ) : null}
              {f.fix ? <span className="pl-5">{fix(f)}</span> : null}
            </li>
          ))}
        </ul>
      ) : p.legal ? (
        <p className="text-[12.5px] flex items-center gap-1.5" style={{ color: LOOK.ok }} data-testid="readiness-ok">
          <Check size={14} /> Ready to play.
        </p>
      ) : null}
    </div>
  );
}
